<template>
  <v-dialog :value="value" max-width="900" @input="$emit('input', $event)">
    <v-card :loading="isLoading" max-width="900">
      <v-card-title class="text-h6">
        {{ $t("FIELD_TAGS.CHILDREN_TITLE") }}
      </v-card-title>
      <v-card-text>
        <div
          class="field-tag-tree"
          :style="{
            '--box-width': `${boxWidth}px`,
            overflowX: 'auto',
            padding: '8px 4px 18px',
          }"
        >
          <div
            class="field-tag-tree__canvas"
            style="display: flex; flex-direction: column; align-items: center; min-width: max-content;"
          >
            <div
              class="field-tag-tree__node field-tag-tree__node--current"
              style="width: var(--box-width); min-height: 84px; box-sizing: border-box; padding: 16px 20px; border: 2px solid #536dfe; border-radius: 12px; background: linear-gradient(145deg, #eef1ff, #ffffff); box-shadow: 0 7px 18px rgba(63, 81, 181, 0.18); text-align: center; display: flex; flex-direction: column; justify-content: center;"
            >
              <div class="text-overline mb-1">{{ $t("FIELD_TAGS.CURRENT_TAG") }}</div>
              <div class="font-weight-bold text-body-1 text-break">
                {{ fieldTag ? fieldTag.value : "-" }}
              </div>
            </div>

            <template v-if="children.length">
              <div
                class="field-tag-tree__connectors"
                aria-hidden="true"
                :style="{
                  width: `${children.length * boxWidth + (children.length - 1) * 16}px`,
                  height: '44px',
                  color: '#78909c',
                }"
              >
                <svg
                  width="100%"
                  height="44"
                  :viewBox="`0 0 ${children.length * boxWidth + (children.length - 1) * 16} 44`"
                  focusable="false"
                  style="display: block; overflow: visible;"
                >
                  <defs>
                    <marker
                      id="field-tag-arrow"
                      markerWidth="7"
                      markerHeight="7"
                      refX="5"
                      refY="3.5"
                      orient="auto"
                    >
                      <path d="M0,0 L7,3.5 L0,7 Z" fill="currentColor" />
                    </marker>
                  </defs>
                  <line
                    :x1="(children.length * boxWidth + (children.length - 1) * 16) / 2"
                    y1="0"
                    :x2="(children.length * boxWidth + (children.length - 1) * 16) / 2"
                    y2="14"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <line
                    :x1="boxWidth / 2"
                    y1="14"
                    :x2="boxWidth / 2 + (children.length - 1) * (boxWidth + 16)"
                    y2="14"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <line
                    v-for="(child, index) in children"
                    :key="`connector-${child.id || index}`"
                    :x1="boxWidth / 2 + index * (boxWidth + 16)"
                    y1="14"
                    :x2="boxWidth / 2 + index * (boxWidth + 16)"
                    y2="42"
                    stroke="currentColor"
                    stroke-width="1.5"
                    marker-end="url(#field-tag-arrow)"
                  />
                </svg>
              </div>

              <div
                class="field-tag-tree__children"
                :style="{
                  gridTemplateColumns: `repeat(${children.length}, var(--box-width))`,
                  display: 'grid',
                  gap: '16px',
                  width: `${children.length * boxWidth + (children.length - 1) * 16}px`,
                  justifyContent: 'center',
                }"
              >
                <div
                  v-for="(child, index) in children"
                  :key="child.id || index"
                  class="field-tag-tree__node field-tag-tree__node--child"
                  style="width: 100%; min-height: 82px; box-sizing: border-box; position: relative; padding: 18px 16px 14px; border: 1px solid #b0bec5; border-radius: 10px; background: #ffffff; box-shadow: 0 4px 12px rgba(55, 71, 79, 0.12); text-align: center; display: flex; flex-direction: column; justify-content: center; align-items: center;"
                >
                  <div
                    class="field-tag-tree__index"
                    style="position: absolute; top: 7px; left: 9px; width: 20px; height: 20px; border-radius: 50%; background: #e8eaf6; color: #3949ab; font-size: 11px; line-height: 20px; font-weight: 700;"
                  >{{ index + 1 }}</div>
                  <div class="font-weight-medium text-body-1 text-break">
                    {{ child.value || child.name }}
                  </div>
                  <div v-if="child.id" class="text-caption grey--text text--darken-1 mt-1">
                    #{{ child.id }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <v-alert v-if="!children.length && !isLoading" dense outlined type="info">
          {{ $t("FIELD_TAGS.NO_CHILDREN") }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">{{ $t("FIELD_TAGS.CANCEL_BUTTON") }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ShowChildrenDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    fieldTag: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      children: [],
      isLoading: false,
      boxWidth: 180,
    };
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.loadChildren();
        return;
      }

      this.children = [];
      this.boxWidth = 180;
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    loadChildren() {
      if (!this.fieldTag) {
        return;
      }

      this.children = this.fieldTag.children || [];
      const longestValueLength = this.children.reduce(
        (longestLength, child) =>
          Math.max(longestLength, String(child.value || child.name || '').length),
        0
      );

      this.boxWidth = Math.max(180, (longestValueLength + 6) * 8);
    },
  },
};
</script>
